import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, MapPin, Sparkles, ShieldCheck, Award, Activity, Crosshair, Globe2, BadgeCheck, Leaf } from "lucide-react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import FAQ from "@/components/FAQ";
import ClinicLocatorMini from "@/components/ClinicLocatorMini";
import Seo from "@/components/Seo";
import { BEFORE_AFTER, BLOG_POSTS } from "@/lib/content";

const TREATMENT_AREAS = [
    { name: "Double Chin", note: "Submental contouring" },
    { name: "Abdomen", note: "Localized sculpting" },
    { name: "Flanks", note: "Love handles" },
    { name: "Arms", note: "Upper-arm definition" },
    { name: "Thighs", note: "Inner-thigh refinement" },
    { name: "Bra Fat", note: "Back & side smoothing" },
    { name: "Knees", note: "Knee-area contouring" },
    { name: "Back Rolls", note: "Posterior sculpting" },
];

const REASONS = [
    { icon: Award, title: "The Original Aqualyx", text: "The authentic European-developed treatment, since 2009." },
    { icon: ShieldCheck, title: "Medsafe Registered", text: "Class III medical device, registered in New Zealand." },
    { icon: Activity, title: "Non-Surgical", text: "No incisions, minimal downtime, in-clinic procedure." },
    { icon: Leaf, title: "Minimal Downtime", text: "Return to your routine the same day." },
    { icon: Crosshair, title: "Precision Treatment", text: "Targeted localized fat reduction in millimetres." },
    { icon: Globe2, title: "Trusted Internationally", text: "65+ countries. 4M+ vials. 15+ years of experience." },
    { icon: BadgeCheck, title: "Certified Providers Only", text: "Available exclusively through Aqualyx Certified Clinics." },
    { icon: Sparkles, title: "Progressive Natural Results", text: "Refined contours that develop over weeks, not hours." },
];

const STEPS = [
    { n: "01", title: "Consultation", text: "A confidential discussion of goals, history and candidacy with a certified Aqualyx provider." },
    { n: "02", title: "Assessment", text: "A clinical mapping of treatment zones, volume and session count." },
    { n: "03", title: "Precision Injections", text: "Aqualyx is administered through micro-injections into the target subcutaneous fat." },
    { n: "04", title: "Fat Breakdown", text: "Aqualyx disrupts fat cell membranes; contents are gradually metabolised." },
    { n: "05", title: "Progressive Contouring", text: "Refinement appears over 3–4 weeks and continues over months." },
    { n: "06", title: "Follow-Up", text: "Subsequent sessions are scheduled every 4–6 weeks until contouring goals are met." },
];

export default function Home() {
    return (
        <div className="page-fade">
            <Seo
                title="Aqualyx New Zealand — The Original Injectable Fat Reduction"
                description="Aqualyx NZ. Non-surgical injectable fat reduction available only through Aqualyx Certified Clinics across New Zealand — Auckland, Wellington, Christchurch, Queenstown. Medsafe registered. Original European treatment."
                path="/"
                breadcrumbs={[{ name: "Home", path: "/" }]}
                schema={{
                    "@context": "https://schema.org",
                    "@graph": [
                        {
                            "@type": "MedicalBusiness",
                            "name": "Aqualyx New Zealand",
                            "url": "https://aqualyxnz.co.nz/",
                            "medicalSpecialty": ["AestheticMedicine", "PlasticSurgery"],
                            "areaServed": { "@type": "Country", "name": "New Zealand" },
                            "description": "The original injectable fat reduction treatment available only through Aqualyx Certified Clinics in New Zealand. Medsafe registered Class III medical device."
                        },
                        {
                            "@type": "MedicalProcedure",
                            "name": "Aqualyx Injectable Fat Reduction",
                            "procedureType": "NonInvasiveProcedure",
                            "bodyLocation": "Submental, abdomen, flanks, arms, thighs, knees, bra line, outer thighs",
                            "howPerformed": "Localized subcutaneous injection of deoxycholate-based solution (Aqualyx) by a certified medical professional, typically across 2–6 sessions spaced 4–6 weeks apart.",
                            "preparation": "Consultation and clinical assessment with an Aqualyx Certified provider.",
                            "followup": "Progressive contouring over 3–4 weeks; follow-up sessions as required.",
                            "status": "Available",
                            "indication": { "@type": "MedicalIndication", "name": "Localized subcutaneous fat" }
                        }
                    ]
                }}
            />

            {/* HERO */}
            <section data-testid="hero" className="relative pt-32 md:pt-40 pb-20 md:pb-32 overflow-hidden">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7 space-y-8">
                        <div className="eyebrow flex items-center gap-3">
                            <span className="w-12 h-px bg-champagne" />
                            <span>Medsafe Registered · Class III</span>
                        </div>
                        <h1 className="editorial-headline text-6xl md:text-8xl lg:text-9xl">
                            Aqualyx<br />
                            <span className="italic text-champagne">New Zealand.</span>
                        </h1>
                        <p className="font-serif italic text-2xl md:text-3xl text-ink/70 max-w-xl leading-snug">
                            The original injectable fat reduction treatment.
                        </p>
                        <p className="text-sm md:text-base text-ink/60 max-w-md leading-relaxed">
                            Non-surgical contouring for stubborn localized fat. Available exclusively through certified clinics across New Zealand.
                        </p>
                        <div className="flex flex-wrap gap-3 pt-4">
                            <Link to="/clinics" data-testid="hero-find-clinic" className="btn-primary">
                                Find Certified Clinic <ArrowRight size={14} strokeWidth={1.5} />
                            </Link>
                            <Link to="/contact" data-testid="hero-book" className="btn-outline">
                                Book Consultation
                            </Link>
                            <Link to="/results" data-testid="hero-results" className="btn-champagne">
                                View Results
                            </Link>
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div className="relative aspect-[4/5] overflow-hidden">
                            <img
                                src="https://dermalis.co.nz/aqualyx-hero.png"
                                alt="Aqualyx body contouring"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="absolute -bottom-6 -left-6 md:-left-12 bg-bone border border-champagne/40 p-6 max-w-[240px]">
                            <div className="eyebrow text-champagne mb-2">Certified Only</div>
                            <p className="font-serif text-lg leading-tight">
                                The original Aqualyx is available only through Aqualyx Certified Clinics in New Zealand.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Stats marquee */}
                <div className="mt-24 border-y border-champagne/30 py-6 overflow-hidden">
                    <div className="marquee-track">
                        {[...Array(2)].map((_, i) => (
                            <div key={i} className="flex gap-16 items-center">
                                <span className="font-serif text-3xl md:text-4xl">4M+ <span className="text-sm uppercase tracking-[0.2em] text-ink/50 ml-2">Vials sold</span></span>
                                <span className="text-champagne">◇</span>
                                <span className="font-serif text-3xl md:text-4xl">15 yrs <span className="text-sm uppercase tracking-[0.2em] text-ink/50 ml-2">On market</span></span>
                                <span className="text-champagne">◇</span>
                                <span className="font-serif text-3xl md:text-4xl">65+ <span className="text-sm uppercase tracking-[0.2em] text-ink/50 ml-2">Countries</span></span>
                                <span className="text-champagne">◇</span>
                                <span className="font-serif text-3xl md:text-4xl italic">Medsafe <span className="text-sm uppercase tracking-[0.2em] text-ink/50 ml-2">NZ Registered</span></span>
                                <span className="text-champagne">◇</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHAT IS */}
            <section id="what-is-aqualyx" data-testid="what-is" className="py-24 md:py-40 bg-nude">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5 lg:sticky lg:top-32 self-start space-y-6">
                        <div className="eyebrow">Overview · The Science</div>
                        <h2 className="editorial-headline text-5xl md:text-6xl">
                            A precision<br />contouring<br /><span className="italic text-champagne">protocol.</span>
                        </h2>
                        <p className="text-ink/70 leading-relaxed max-w-md">
                            Aqualyx is a Class III injectable medical device based on the deoxycholate family of acids — designed to dissolve localized fat cells progressively, sculpting the body without surgery.
                        </p>
                        <p className="text-ink/60 text-sm leading-relaxed max-w-md">
                            Each authentic vial carries a digitally numbered hologram for complete traceability, ensuring patients receive the original European-manufactured treatment.
                        </p>
                        <img
                            src="https://dermalis.co.nz/aqualyx-product.png"
                            alt="Aqualyx product"
                            className="w-3/4 mt-8"
                        />
                    </div>

                    <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-champagne/30">
                        {TREATMENT_AREAS.map((a, i) => (
                            <div
                                key={a.name}
                                data-testid={`treatment-area-${i}`}
                                className="bg-nude p-8 md:p-10 group hover:bg-bone transition-colors duration-500"
                            >
                                <div className="number-stroke text-5xl md:text-6xl mb-6">{String(i + 1).padStart(2, "0")}</div>
                                <h3 className="font-serif text-2xl md:text-3xl mb-2">{a.name}</h3>
                                <p className="text-xs uppercase tracking-[0.2em] text-ink/50">{a.note}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY CHOOSE */}
            <section data-testid="why-choose" className="py-24 md:py-40">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-5">
                            <div className="eyebrow mb-4">Why Aqualyx</div>
                            <h2 className="editorial-headline text-5xl md:text-7xl">
                                Eight reasons<br />it remains<br /><span className="italic">unrivalled.</span>
                            </h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                            <p className="text-ink/70 leading-relaxed">
                                Aqualyx is the most established injectable fat reduction protocol in the world. When you choose an Aqualyx Certified Clinic, you choose verified product, trained practitioners, and an internationally proven contouring outcome.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-champagne/30 border border-champagne/30">
                        {REASONS.map((r, i) => {
                            const Icon = r.icon;
                            return (
                                <div
                                    key={r.title}
                                    data-testid={`reason-${i}`}
                                    className="bg-bone p-8 md:p-10 space-y-5 hover:bg-nude transition-colors duration-500"
                                >
                                    <Icon strokeWidth={1} size={32} className="text-champagne" />
                                    <h3 className="font-serif text-2xl leading-tight">{r.title}</h3>
                                    <p className="text-sm text-ink/60 leading-relaxed">{r.text}</p>
                                </div>
                            );
                        })}
                    </div>

                    <div data-testid="beware-banner" className="mt-12 border border-champagne/40 bg-nude p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
                        <div>
                            <div className="eyebrow text-champagne mb-2">Authenticity Advisory</div>
                            <p className="font-serif text-2xl md:text-3xl italic max-w-3xl leading-snug">
                                Beware of imitations. The original Aqualyx treatment is available only through officially certified Aqualyx clinics in New Zealand.
                            </p>
                        </div>
                        <Link to="/clinics" className="btn-primary whitespace-nowrap">Verify a Clinic</Link>
                    </div>
                </div>
            </section>

            {/* BEFORE / AFTER */}
            <section data-testid="results-preview" className="py-24 md:py-40 bg-nude">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-7">
                            <div className="eyebrow mb-4">Results</div>
                            <h2 className="editorial-headline text-5xl md:text-7xl">
                                Before & after<br /><span className="italic text-champagne">results.</span>
                            </h2>
                        </div>
                        <div className="lg:col-span-4 lg:col-start-9 flex items-end">
                            <p className="text-ink/70 leading-relaxed">
                                Real patient cases from Aqualyx Certified Clinics. Individual results may vary.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        {BEFORE_AFTER.map((b, i) => (
                            <div key={b.title} data-testid={`ba-card-${i}`} className="lg:col-span-8 lg:col-start-3 space-y-4">
                                <BeforeAfterSlider src={b.src} layout={b.layout} alt={b.title} />
                                <div className="flex items-center justify-between pt-2">
                                    <div>
                                        <div className="font-serif text-2xl md:text-3xl">{b.title}</div>
                                        <div className="eyebrow mt-1">{b.area} · {b.sessions}</div>
                                    </div>
                                    <div className="eyebrow text-ink/40 hidden md:block">Individual results may vary</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-16">
                        <Link to="/results" data-testid="see-all-results" className="btn-outline">
                            Explore Full Gallery <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section data-testid="how-it-works" className="py-24 md:py-40">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
                        <div className="lg:col-span-6">
                            <div className="eyebrow mb-4">The Journey</div>
                            <h2 className="editorial-headline text-5xl md:text-7xl">
                                Six considered<br /><span className="italic">steps.</span>
                            </h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-champagne/30 border border-champagne/30">
                        {STEPS.map((s, i) => (
                            <div
                                key={s.n}
                                data-testid={`step-${i}`}
                                className="bg-bone p-10 md:p-12 space-y-6 relative"
                            >
                                <div className="flex items-baseline gap-4">
                                    <span className="number-stroke text-7xl md:text-8xl">{s.n}</span>
                                </div>
                                <h3 className="font-serif text-3xl">{s.title}</h3>
                                <p className="text-sm text-ink/60 leading-relaxed">{s.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CLINIC LOCATOR PREVIEW */}
            <section data-testid="clinics-section" className="py-24 md:py-40 bg-ink text-bone">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-7">
                            <div className="eyebrow text-champagne mb-4">Certified Clinics · New Zealand</div>
                            <h2 className="editorial-headline text-5xl md:text-7xl">
                                Find your<br /><span className="italic text-champagne">official provider.</span>
                            </h2>
                        </div>
                        <div className="lg:col-span-4 lg:col-start-9 flex items-end">
                            <p className="text-bone/70 leading-relaxed">
                                Only certified clinics offer original Aqualyx in New Zealand. Locate yours below.
                            </p>
                        </div>
                    </div>

                    <ClinicLocatorMini />

                    <div className="text-center mt-12">
                        <Link to="/clinics" className="btn-champagne">
                            <MapPin size={14} strokeWidth={1.5} /> Open Full Locator
                        </Link>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section data-testid="faq-section" className="py-24 md:py-40">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
                        <div className="eyebrow mb-4">Frequently Asked</div>
                        <h2 className="editorial-headline text-5xl md:text-7xl">
                            Answers, in<br /><span className="italic text-champagne">confidence.</span>
                        </h2>
                        <p className="text-ink/60 mt-6 max-w-sm leading-relaxed">
                            Curated answers to the most asked questions about Aqualyx in New Zealand. For tailored advice, book a consultation with a certified provider.
                        </p>
                    </div>
                    <div className="lg:col-span-7">
                        <FAQ />
                    </div>
                </div>
            </section>

            {/* ABOUT */}
            <section data-testid="about-section" className="py-24 md:py-40 bg-nude">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-6">
                        <img src="https://dermalis.co.nz/aqualyx-duo.png" alt="Aqualyx and Aqualyx Face" className="w-full" />
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 space-y-6">
                        <div className="eyebrow">About</div>
                        <h2 className="editorial-headline text-4xl md:text-6xl">
                            A New Zealand<br /><span className="italic">presence.</span>
                        </h2>
                        <p className="text-ink/70 leading-relaxed">
                            Aqualyx New Zealand is the consumer destination for the original injectable fat reduction treatment. We exist to verify, educate, and connect patients with the country's network of Aqualyx Certified Clinics.
                        </p>
                        <p className="text-ink/60 text-sm leading-relaxed">
                            Aqualyx™ injection non-surgically reduces localized fat deposits. Aqualyx™ is a Class III medical device. Medsafe registered in New Zealand. Always consult a qualified healthcare professional.
                        </p>
                        <Link to="/about" className="btn-outline">Read More</Link>
                    </div>
                </div>
            </section>

            {/* BLOG PREVIEW */}
            <section data-testid="blog-preview" className="py-24 md:py-40">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="eyebrow mb-4">Journal</div>
                            <h2 className="editorial-headline text-5xl md:text-6xl">Recent reading.</h2>
                        </div>
                        <Link to="/blog" className="btn-outline">All Articles</Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {BLOG_POSTS.slice(0, 3).map((p, i) => (
                            <Link to={`/blog/${p.slug}`} key={p.slug} data-testid={`blog-card-${i}`} className="block group">
                                <div className="aspect-[4/3] overflow-hidden mb-5">
                                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                </div>
                                <div className="eyebrow mb-2">{p.category} · {p.read}</div>
                                <h3 className="font-serif text-2xl leading-tight mb-2 group-hover:text-champagne transition-colors">{p.title}</h3>
                                <p className="text-sm text-ink/60 leading-relaxed">{p.excerpt}</p>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* MANDATORY STATEMENT */}
            <section className="py-16 md:py-24 bg-ink text-bone">
                <div className="max-w-3xl mx-auto px-6 md:px-10 text-center space-y-6">
                    <div className="eyebrow text-champagne">Mandatory Statement</div>
                    <p className="font-serif italic text-xl md:text-2xl leading-relaxed text-bone/90">
                        Aqualyx™ injection non-surgically reduces localized fat deposits. Aqualyx™ is a Class III medical device. Aqualyx™ has risks and benefits. Consult your healthcare professional to determine if Aqualyx™ is right for you. Medsafe registered in New Zealand.
                    </p>
                </div>
            </section>
        </div>
    );
}
